import React from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';

// Lucide Icons
import {
    BarChart3,
    CheckCircle2,
    Clock,
    AlertCircle,
    TrendingUp,
    Link2,
    Smartphone,
    ExternalLink,
    FileText,
    Hash
} from 'lucide-react';

// Components
import Loader from '../../Loader';

// Styles
import s from './SocialAnalyticsDashboard.css';

// GraphQL
import getSocialAnalytics from './getSocialAnalytics.graphql';

class SocialAnalyticsDashboard extends React.Component {
    static propTypes = {
        data: PropTypes.shape({
            loading: PropTypes.bool,
            getSocialAnalytics: PropTypes.object,
        }),
    };

    renderStatCard(title, value, IconComponent, color) {
        return (
            <div className={s.statCard} style={{ borderLeftColor: color }}>
                <div className={s.statIcon} style={{ backgroundColor: color }}>
                    <IconComponent size={24} color="white" />
                </div>
                <div className={s.statContent}>
                    <div className={s.statValue}>{value}</div>
                    <div className={s.statTitle}>{title}</div>
                </div>
            </div>
        );
    }

    renderPlatformStats(platforms) {
        if (!platforms || platforms.length === 0) {
            return <div className={s.emptyState}>No hay canales conectados</div>;
        }

        return (
            <div className={s.platformGrid}>
                {platforms.map((platform, index) => (
                    <div key={index} className={s.platformCard}>
                        <img
                            src={platform.picture}
                            alt={platform.name}
                            className={s.platformPicture}
                        />
                        <div className={s.platformInfo}>
                            <div className={s.platformName}>{platform.name}</div>
                            <div className={s.platformType}>{platform.identifier}</div>
                            <div className={cx(s.platformStatus, platform.disabled ? s.disabled : s.active)}>
                                {platform.disabled ? (
                                    <><Clock size={12} /> Pausado</>
                                ) : (
                                    <><CheckCircle2 size={12} /> Activo</>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderRecentPosts(posts) {
        if (!posts || posts.length === 0) {
            return <div className={s.emptyState}>No hay publicaciones recientes</div>;
        }

        const getStateIcon = (state) => {
            switch (state) {
                case 'PUBLISHED': return <CheckCircle2 size={14} className={s.iconPublished} />;
                case 'QUEUE': return <Clock size={14} className={s.iconQueue} />;
                case 'ERROR': return <AlertCircle size={14} className={s.iconError} />;
                case 'DRAFT': return <FileText size={14} className={s.iconDraft} />;
                default: return <Hash size={14} />;
            }
        };

        const getStateLabel = (state) => {
            switch (state) {
                case 'PUBLISHED': return 'Publicado';
                case 'QUEUE': return 'En Cola';
                case 'ERROR': return 'Error';
                case 'DRAFT': return 'Borrador';
                default: return state;
            }
        };

        return (
            <div className={s.postsTable}>
                <div className={s.tableHeader}>
                    <div className={s.colPlatform}>Plataforma</div>
                    <div className={s.colContent}>Contenido</div>
                    <div className={s.colDate}>Fecha</div>
                    <div className={s.colStatus}>Estado</div>
                    <div className={s.colAction}>Enlace</div>
                </div>
                {posts.map((post, index) => (
                    <div key={index} className={s.tableRow}>
                        <div className={s.colPlatform}>
                            <img
                                src={post.integration?.picture}
                                alt={post.integration?.name}
                                className={s.postPlatformIcon}
                            />
                            <span>{post.integration?.providerIdentifier}</span>
                        </div>
                        <div className={s.colContent}>
                            {post.content?.substring(0, 80)}...
                        </div>
                        <div className={s.colDate}>
                            {new Date(post.publishDate).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </div>
                        <div className={s.colStatus}>
                            <span
                                className={cx(s.statusBadge, s[`status${post.state}`])}
                                title={post.state === 'ERROR' && post.error ? post.error : ''}
                            >
                                {getStateIcon(post.state)} {getStateLabel(post.state)}
                            </span>
                        </div>
                        <div className={s.colAction}>
                            {post.releaseURL ? (
                                <a
                                    href={post.releaseURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={s.viewLink}
                                >
                                    Ver <ExternalLink size={12} />
                                </a>
                            ) : (
                                <span className={s.noLink}>-</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { data } = this.props;

        if (data.loading) {
            return <Loader type="text" />;
        }

        const analytics = data?.getSocialAnalytics || {};
        const {
            totalPosts = 0,
            publishedPosts = 0,
            errorPosts = 0,
            queuedPosts = 0,
            channels = [],
            recentPosts = []
        } = analytics;

        const successRate = totalPosts > 0
            ? Math.round((publishedPosts / totalPosts) * 100)
            : 0;

        return (
            <div className={s.container}>
                <div className={s.pageHeader}>
                    <BarChart3 size={32} className={s.pageIcon} />
                    <div>
                        <h1 className={s.pageTitle}>Social Media Analytics</h1>
                        <p className={s.pageSubtitle}>
                            Monitorea el rendimiento de tus publicaciones en redes sociales
                        </p>
                    </div>
                </div>

                {/* Stats Summary */}
                <div className={s.statsGrid}>
                    {this.renderStatCard('Total Posts', totalPosts, Smartphone, '#3b82f6')}
                    {this.renderStatCard('Publicados', publishedPosts, CheckCircle2, '#22c55e')}
                    {this.renderStatCard('En Cola', queuedPosts, Clock, '#f59e0b')}
                    {this.renderStatCard('Errores', errorPosts, AlertCircle, '#ef4444')}
                    {this.renderStatCard('Tasa de Éxito', `${successRate}%`, TrendingUp, '#8b5cf6')}
                    {this.renderStatCard('Canales Activos', channels.filter(c => !c.disabled).length, Link2, '#06b6d4')}
                </div>

                {/* Channels Section */}
                <div className={s.section}>
                    <h2 className={s.sectionTitle}>
                        <Link2 size={20} className={s.sectionIcon} />
                        Canales Conectados
                    </h2>
                    {this.renderPlatformStats(channels)}
                </div>

                {/* Recent Posts Section */}
                <div className={s.section}>
                    <h2 className={s.sectionTitle}>
                        <FileText size={20} className={s.sectionIcon} />
                        Publicaciones Recientes
                    </h2>
                    {this.renderRecentPosts(recentPosts)}
                </div>
            </div>
        );
    }
}

export default compose(
    injectIntl,
    withStyles(s),
    graphql(getSocialAnalytics, {
        options: {
            fetchPolicy: 'network-only',
        },
    }),
)(SocialAnalyticsDashboard);
